import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNotifications } from "@/hooks/useNotifications";
import { relativeTime } from "@/lib/notifications";
import type { NotificationType } from "@/components/feature/Toast";

const TYPE_META: Record<NotificationType, { icon: string; className: string }> = {
  success: {
    icon: "ri-checkbox-circle-fill",
    className: "bg-secondary-100 text-secondary-600",
  },
  warning: {
    icon: "ri-alert-fill",
    className: "bg-primary-100 text-primary-600",
  },
  error: {
    icon: "ri-close-circle-fill",
    className: "bg-accent-100 text-accent-600",
  },
  info: {
    icon: "ri-information-fill",
    className: "bg-background-200 text-foreground-600",
  },
  pending: {
    icon: "ri-time-fill",
    className: "bg-secondary-100 text-secondary-600",
  },
  security: {
    icon: "ri-shield-keyhole-fill",
    className: "bg-foreground-950 text-background-50",
  },
  celebration: {
    icon: "ri-trophy-fill",
    className: "bg-primary-500 text-white",
  },
};

type FilterKey = "all" | "unread";

export default function NotificationsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    notifications,
    unreadCount,
    loading,
    error,
    refresh,
    markRead,
    markAllRead,
    remove,
  } = useNotifications();

  const [filter, setFilter] = useState<FilterKey>("all");

  const filtered = useMemo(() => {
    if (filter === "unread") return notifications.filter((n) => !n.read);
    return notifications;
  }, [notifications, filter]);

  const handleClick = (id: string, read: boolean, actionUrl: string | null) => {
    if (!read) markRead(id);
    if (actionUrl) navigate(actionUrl);
  };

  return (
    <div className="min-h-screen bg-background-50">
      <main className="mx-auto w-full max-w-3xl px-4 py-8 md:px-6">
        {/* Encabezado */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100">
              <i className="ri-notification-3-line text-xl text-primary-500"></i>
            </span>
            <div>
              <h1 className="font-heading text-xl font-bold text-foreground-950">
                {t("notifications.title")}
              </h1>
              <p className="text-sm text-foreground-600">
                {t("notifications.subtitle")}
              </p>
            </div>
          </div>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllRead}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap self-start rounded-md border border-background-200 bg-background-50 px-3 py-2 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100 sm:ml-auto sm:self-auto"
            >
              <i className="ri-check-double-line"></i>
              {t("notifications.markAll")}
            </button>
          )}
        </div>

        {/* Filtros */}
        <div className="inline-flex items-center gap-1 rounded-full border border-background-200/70 bg-background-100 p-1">
          <button
            type="button"
            onClick={() => setFilter("all")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              filter === "all"
                ? "bg-primary-500 text-white"
                : "text-foreground-500 hover:text-foreground-900"
            }`}
          >
            {t("notifications.all")}
          </button>
          <button
            type="button"
            onClick={() => setFilter("unread")}
            className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              filter === "unread"
                ? "bg-primary-500 text-white"
                : "text-foreground-500 hover:text-foreground-900"
            }`}
          >
            {t("notifications.unread")}
            {unreadCount > 0 && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-xs font-bold ${
                  filter === "unread"
                    ? "bg-white/20 text-white"
                    : "bg-accent-500 text-white"
                }`}
              >
                {unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* Contenido */}
        <div className="mt-4 space-y-2">
          {loading ? (
            <div className="flex flex-col items-center justify-center gap-3 px-6 py-16 text-center">
              <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
              <p className="text-sm text-foreground-500">Cargando…</p>
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100">
                <i className="ri-error-warning-line text-2xl text-accent-700"></i>
              </span>
              <p className="mt-4 text-sm font-semibold text-foreground-900">
                Ocurrió un error
              </p>
              <p className="mt-1 max-w-sm text-sm text-foreground-500">{error}</p>
              <button
                type="button"
                onClick={refresh}
                className="mt-4 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600"
              >
                Reintentar
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-50 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-notification-off-line text-2xl text-foreground-400"></i>
              </span>
              <p className="mt-4 text-sm font-semibold text-foreground-900">
                {t("notifications.empty")}
              </p>
              <p className="mt-1 text-sm text-foreground-500">
                {t("notifications.emptyDesc")}
              </p>
            </div>
          ) : (
            filtered.map((n) => {
              const meta = TYPE_META[n.type] ?? TYPE_META.info;
              return (
                <div
                  key={n.id}
                  onClick={() => handleClick(n.id, n.read, n.action_url)}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors ${
                    n.read
                      ? "border-background-200/70 bg-background-50 hover:bg-background-100"
                      : "border-primary-200 bg-primary-50/60 hover:bg-primary-50"
                  }`}
                >
                  <span
                    className={`mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${meta.className}`}
                  >
                    <i className={`${meta.icon} text-base`}></i>
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p
                        className={`text-sm ${
                          n.read ? "font-semibold text-foreground-800" : "font-bold text-foreground-950"
                        }`}
                      >
                        {n.title}
                      </p>
                      {!n.read && (
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-primary-500"></span>
                      )}
                    </div>
                    <p className="mt-0.5 text-sm leading-snug text-foreground-600">
                      {n.message}
                    </p>
                    <div className="mt-1.5 flex items-center gap-3">
                      <span className="text-xs text-foreground-400">
                        {relativeTime(n.created_at)}
                      </span>
                      {n.action_url && (
                        <span className="text-xs font-semibold text-primary-600">
                          Ver solicitud
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(n.id);
                    }}
                    aria-label="Eliminar notificación"
                    className="flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full opacity-50 transition-opacity hover:opacity-100"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                </div>
              );
            })
          )}
        </div>
      </main>
    </div>
  );
}